<template>
  <div class="search-drawer critical-component-clear">
    <div class="search-drawer__content-container">
      <form
        class="search-drawer__form"
        role="search"
        @submit.prevent="onSubmit"
      >
        <div class="search-drawer__form-inner">
          <input
            ref="inputField"
            v-model.trim="input"
            class="search-drawer__input text-desktop-regular"
            type="text"
            autocomplete="off"
            :aria-label="$string('search.placeholder')"
            :placeholder="$string('search.placeholder')"
            @input="updateSearchQuery"
          >

          <transition-height>
            <p
              v-show="!isMobile && noResults"
              class="
                search-drawer__no-results
                search-drawer__no-results--desktop
                text-desktop-regular
              "
              v-text="
                $string('search.no_results', {
                  replace: {
                    query: query,
                  }
                })
              "
            />
          </transition-height>
        </div>
      </form>

      <div
        v-if="showDefault"
        class="search-drawer__popular-searches"
      >
        <p
          class="
            search-drawer__popular-searches__title
            text-mobile-regular-mobile
            text-desktop-small-desktop
          "
          v-text="$string('search.popular_searches')"
        />

        <ul class="search-drawer__popular-searches__items">
          <li
            v-for="(item, index) of popularSearches"
            :key="index"
            class="search-drawer__popular-searches__item"
          >
            <a
              :href="(`/search?q=${encodeURI(stripHTML(item.label || item.title)).toLowerCase()}`)"
              class="search-drawer__popular-searches__link"
            >
              <span v-html="item.label || item.title" />
            </a>
          </li>
        </ul>
      </div>

      <div
        v-if="showDefault && whatsNew.length > 0"
        class="search-drawer__suggestions"
      >
        <p
          class="
            search-drawer__suggestions__title
            text-mobile-regular-mobile
            text-desktop-small-desktop
          "
          v-text="$string('search.whats_new')"
        />

        <ul class="search-drawer__suggestions__items">
          <li
            v-for="(item, index) of whatsNew"
            :key="index"
            class="search-drawer__suggestions__item"
          >
            <a
              :href="item.url"
              class="search-drawer__suggestions__link"
            >
              <span v-html="item.label || item.title" />
            </a>
          </li>
        </ul>
      </div>

      <div
        v-if="autoSuggestions.length"
        class="search-drawer__suggestions"
      >
        <ul class="search-drawer__suggestions__items">
          <li
            v-for="(item, index) of autoSuggestions"
            :key="index"
            class="search-drawer__suggestions__item"
          >
            <a
              :href="(`/search?q=${encodeURI(stripHTML(item.query))}`)"
              class="search-drawer__suggestions__link"
            >
              <span v-html="item.query" />
            </a>
          </li>
        </ul>
      </div>

      <div
        v-if="contentSuggestions.length > 0"
        class="search-drawer__suggestions"
      >
        <p
          class="
            search-drawer__suggestions__title
            text-mobile-regular-mobile
            text-desktop-small-desktop
          "
          v-text="$string('search.content_search_links_title')"
        />

        <ul class="search-drawer__suggestions__items">
          <li
            v-for="(item, index) of contentSuggestions"
            :key="index"
            class="search-drawer__suggestions__item"
          >
            <a
              :href="item.page_url"
              class="search-drawer__suggestions__link"
            >
              <span
                class="marked-item"
                v-html="markedItem(item)"
              />
            </a>
          </li>
        </ul>
      </div>

      <div
        v-if="collectionSuggestions.length > 0"
        class="search-drawer__suggestions"
      >
        <p
          class="
            search-drawer__suggestions__title
            text-mobile-regular-mobile
            text-desktop-small-desktop
          "
          v-text="$string('search.collection_search_links_title')"
        />

        <ul class="search-drawer__suggestions__items">
          <li
            v-for="(item, index) of collectionSuggestions"
            :key="index"
            class="search-drawer__suggestions__item"
          >
            <a
              :href="`/collections/${item.slug}`"
              class="search-drawer__suggestions__link"
            >
              <span v-html="item.title" />
            </a>
          </li>
        </ul>
      </div>

      <div
        v-if="filters.length"
        class="search-drawer__filters-container"
      >
        <div class="search-drawer__toggle-container">
          <button
            v-if="filters.length"
            type="button"
            class="filter-tabs__toggle search-drawer__filter-toggle"
            @click="toggleFilters"
          >
            <icon-plus v-if="!filtersVisible" />
            <icon-minus v-if="filtersVisible" />
            <span v-text="$string('collection.open_filters')" />
            <!--
              // @TODO: Get correct filter count instead of filter types
              <span
              v-if="selectedFilters.length > 0"
              v-text="`&nbsp;(${selectedFilters.length})`"
              />
            -->
          </button>
        </div>

        <!-- Desktop Filter List -->
        <div
          v-if="filters.length"
          class="search-drawer__filters"
          :class="[
            { 'search-drawer__filters--active': filtersVisible }
          ]"
          js-body-scroll-lock-ignore
        >
          <div
            class="search-drawer__filters-header"
          >
            <p
              class="
                search-drawer__filters-title
                text-desktop-medium-regular-desktop
                text-mobile-medium-regular-mobile
              "
              v-text="$string('collection.filter_tabs_title')"
            />

            <button
              class="search-drawer__filters-close icon-button"
              type="button"
              :aria-label="$string('accessibility.close')"
              @click.prevent="toggleFilters"
            >
              <icon-close />
            </button>
          </div>

          <div class="search-drawer__filters-body">
            <div
              v-for="filter of filters"
              :key="filter.key"
              class="search-drawer__filter-container"
            >
              <div class="search-drawer__filter-header">
                <p
                  class="search-drawer__filter-title text-desktop-small"
                  v-text="filter.label"
                />
              </div>

              <filter-list
                :ref="filter.key"
                block-class="search-drawer"
                :filter="filter"
                :is-selected="filterContainsSelected(filter.key)"
                @filter-selected="handleFilterSelect"
              />
            </div>

            <div class="search-drawer__filters-footer">
              <button
                type="button"
                class="button button--block search-drawer__filters-submit"
                @click="toggleFilters"
              >
                <span
                  class="search-drawer__filters-text"
                  v-text="tabTitle"
                />
              </button>

              <button
                v-if="selectedFilters.length"
                type="button"
                class="button button--secondary button--block search-drawer__filters-reset"
                @click="resetAllFilters"
                v-text="$string('search.reset')"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      ref="resultsContainer"
      class="search-drawer__results"
      @click="handleDrawer"
    >
      <div
        v-if="searchResults.length"
        ref="resultsGrid"
        class="search-drawer__results-grid"
        js-body-scroll-lock-ignore
      >
        <div
          v-for="(product, index) of searchResults"
          :key="`product-card-${product.id}`"
          class="search-drawer__results-item product-card--medium"
        >
          <product-card
            block-class="search-drawer"
            :index="index"
            :product="product"
            :mobile-gallery="false"
          />
        </div>
      </div>
    </div>
  </div>

  <depict-search
    :merchant="merchant"
    :limit="productLimit"
    :offset="offset"
    :cursor="cursor"
    :query="query"
    :selected-filters="selectedFilters"
    @search-updated="displaySearchResults"
  />
</template>

<script>

/**
 * Vue: Search drawer (search-drawer)
 * -----------------------------------------------------------------------------
 * Search drawer component.
 *
 * @param {Boolean} isActive - isActive - If the containing overlay is active.
 * @param {Number} minChars - Minimum number of characters required to display
 * search results.
 * @param {String} merchant - Depict merchant required for the API
 * @param {Number} productLimit - Limit of products to be displayed at once.
 * @param {Array} popularSearches - Menu of default popular searches.
 * @param {Array} whatsNew - Menu of new content.
 *
 */

import { mapActions, mapGetters, mapState } from 'vuex'
import { formatDepictFilters, formatDepictProduct } from '~/helpers/format'
import { throttle } from '~/helpers/general'

import FilterList from '~global/filter-list/filter-list'
import DepictSearch from '~global/depict/depict-search'

import ProductCard from '~async/product-card/product-card'
import TransitionHeight from '~async/transition-height/transition-height'
import IconClose from '~icons/misc/close.svg'
import IconPlus from '~icons/misc/plus-new.svg'
import IconMinus from '~icons/misc/minus-new.svg'

export default {
  name: 'SearchDrawer',

  components: {
    FilterList,
    IconClose,
    IconPlus,
    IconMinus,
    DepictSearch,
    ProductCard,
    TransitionHeight,
  },

  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    minChars: {
      type: Number,
      default: 0,
    },
    merchant: {
      type: String,
      default: '',
    },
    productLimit: {
      type: Number,
      default: 0,
    },
    popularSearches: {
      type: Array,
      default: () => [],
    },
    whatsNew: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      selectedFilters: [],
      filters: [],
      input: '',
      isScrolling: false,
      meta: {},
      noResults: false,
      offset: 0,
      cursor: null,
      query: '',
      scrollPosition: 0,
      searchResults: [],
      contentSuggestions: [],
      autoSuggestions: [],
      collectionSuggestions: [],
      filtersVisible: false,
    }
  },

  computed: {

    showDefault() {
      return this.emptyResultsSet
    },

    emptyResultsSet() {
      return [
        ...this.searchResults,
        ...this.contentSuggestions,
        ...this.collectionSuggestions,
      ].length < 1
    },

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getFirstElement: 'accessibility/getFirstElement',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute filter tab title string
     * @returns {String}
     */
    tabTitle() {
      return this.$string('collection.show_products', {
        replace: {
          num: this.meta?.totalResultsFound ? this.meta.totalResultsFound : '',
        },
      })
    },
  },

  watch: {

    /**
     * Calls functions when drawer is opened
     */
    isActive() {
      if (this.isActive) {
        this.$nextTick().then(() => {
          this.$refs.inputField.focus()
          this.updateFocusTrap()
        })
        return
      }

      this.clearSearch()
    },

    /**
     * Updates no results condition
     */
    searchResults() {
      this.$nextTick().then(() => {
        this.noResults =
          this.input.length > this.minChars && this.emptyResultsSet
      })
    },
  },

  mounted() {
    this.setScrollEventListener()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      setFocusTrap: 'accessibility/setFocusTrap',
      closeOverlay: 'overlays/close',
    }),

    /**
     * Sets the scroll event listener.
     */
    setScrollEventListener() {
      const resultsContainer = this.$refs.resultsContainer

      resultsContainer.addEventListener(
        'scroll',
        throttle(() => this.handleScroll(resultsContainer), 16),
      )
    },

    /**
     * Update focus trap using Vuex.
     */
    updateFocusTrap() {
      this.setFocusTrap({ container: this.$refs.inputField })

      /**
       * Have to use timeout to wait for animation to complete.
       * - Safari doesn't support `preventScroll`.
       */
      setTimeout(() => {
        if (!this.getFirstElement) {
          return
        }

        this.getFirstElement.focus({ preventScroll: true })
      }, this.$timing('normal'))
    },

    /**
     * Function to handle scroll events on page
     * - Detects when end of results grid is reached and updates product offset
     *   value
     * - Updates 'isScrolling' value
     * @param {Element} resultsContainer - Target container element.
     */
    handleScroll(resultsContainer) {
      const container = resultsContainer.getBoundingClientRect()

      const resultsGrid = this.$refs.resultsGrid
      const grid = resultsGrid.getBoundingClientRect()

      const endOfGrid = (grid.bottom - container.bottom - 18) <= 0

      const scrollingUp = this.scrollPosition > grid.top

      if (scrollingUp && endOfGrid) {
        const { noOfResults, offset, totalResultsFound, cursor } = this.meta

        const fetchedProductCount = noOfResults + offset
        if (fetchedProductCount >= totalResultsFound) {
          return
        }

        this.cursor = cursor
        this.offset = Number(fetchedProductCount)
      }

      this.isScrolling = scrollingUp
      this.scrollPosition = grid.top
    },

    /**
     * Reset search modal
     * @param {Boolean} input - Determines if input should be reset.
     */
    clearSearch(input = true) {
      if (input) {
        this.input = ''
      }
      this.offset = 0
      this.query = ''
      this.cursor = null
      this.filters = []
      this.selectedFilters = []
      this.searchResults = []
      this.autoSuggestions = []
      this.collectionSuggestions = []
      this.contentSuggestions = []
      this.filtersVisible = false
    },

    /**
     * Updates Klevu search query based on input value
     * Resets search modal if input field is empty
     */
    updateSearchQuery() {
      if (!this.input || this.input.length < this.minChars) {
        this.clearSearch(false)
        return
      }

      this.query = this.input
      this.cursor = null
      this.offset = 0
    },

    /**
     * Removes HTML elements and returns the cleaned string
     * @param {String} html - String of HTML
     */
    stripHTML(html) {
      const div = document.createElement('div')
      div.innerHTML = html
      return div.innerText
    },

    /**
     * Updates selected filters based on selected checkbox
     * @param {Object} option - Option object containing event, key and value.
     */
    handleFilterSelect(option) {
      const { event, key, value, op } = option
      const filterArray = [...this.selectedFilters]

      // Check if filter object exists in selected filters
      const currentFilter = filterArray.find((filter) => filter.key === key)

      if (currentFilter) {
        // Check if checkbox is selected and the value doesn't already exist
        const isChecked = event.target.checked
        const valueExists = currentFilter.values.includes(value)

        if (isChecked && !valueExists) {
          currentFilter.values.push(value)
        } else if (!isChecked && valueExists) {
          const valueIndex = currentFilter.values.indexOf(value)
          currentFilter.values.splice(valueIndex, 1)

          // Delete filter object from array if no value is selected
          if (!currentFilter.values.length) {
            const filterIndex = filterArray.indexOf(currentFilter)
            filterArray.splice(filterIndex, 1)
          }
        }
      } else {
        // Create new filter object in selected filters
        filterArray.push({
          key,
          values: [value],
          op,
          settings: {
            singleSelect: false,
          },
        })
      }

      // Reassign selectedFilters to trigger Vue reactive state
      this.selectedFilters = filterArray
    },

    /**
     * Check if filter key contains values of selected key
     * @param {String} key - Key value of selected filter.
     * @returns {Boolean}
     */
    filterContainsSelected(key) {
      const currentFilter = this.selectedFilters?.find(
        (filter) => filter.key === key,
      )
      return Boolean(currentFilter?.values?.length)
    },

    /**
     * Clears the current selected filter array
     * @param {String} key - Key value of selected filter.
     */
    resetFilter(key) {
      const filterArray = [...this.selectedFilters]
      const currentFilter = filterArray.find((filter) => filter.key === key)
      const filterIndex = filterArray.indexOf(currentFilter)

      filterArray.splice(filterIndex, 1)
      this.selectedFilters = filterArray
      this.$refs[key]?.forEach((ref) => {
        ref.resetComponentData(key)
      })
    },

    /**
     * Clears all filters .
     */
    resetAllFilters() {
      const filterArray = [...this.selectedFilters]
      const filterKeys = filterArray.map((filter) => {
        return filter.key
      })

      filterKeys.forEach((key) => {
        this.resetFilter(key)
      })
    },

    toggleFilters() {
      this.filtersVisible = !this.filtersVisible
    },

    markedItem(item) {
      const { title = '', highlights = [] } = item

      if (highlights.length > 0) {
        return highlights[0].snippet
      }

      return title
    },

    filterCategoryResult(result) {
      return result.type === 'listing' && result.listing_type === 'category'
    },

    /**
     * Updates search results and filters from Klevu response
     * @param {Object} response - Response object from Klevu API.
     */
    displaySearchResults(response) {
      const {
        data: {
          productResults: {
            n_hits: totalResultsFound = 0,
            filters = [],
            cursor = null,
            content_search_links: contentSuggestions = [],
            displays: products = [],
            offset = 0,
          } = {},
          suggestionResults: { suggestions = [] } = {},
        } = {},
        action = 'default',
      } = response

      this.meta = {
        noOfResults: products.length,
        offset,
        cursor,
        totalResultsFound,
      }

      this.filters = formatDepictFilters(filters, this.selectedFilters)

      const formattedProducts = products?.map((product) => {
        return formatDepictProduct(product)
      })

      if (action === 'offset') {
        this.searchResults = [...this.searchResults, ...formattedProducts]
        return
      }

      this.searchResults = formattedProducts
      this.contentSuggestions = contentSuggestions
      this.autoSuggestions = suggestions.filter(result => result.type === 'query')
      this.collectionSuggestions = suggestions.filter(this.filterCategoryResult)

      this.cursor = cursor
    },

    /**
     * Handles search drawer close event if the search results are empty.
     */
    handleDrawer() {
      if (this.searchResults.length === 0) {
        this.closeOverlay('searchDrawer')
      }
    },
  },
}
</script>

<style lang="scss">
@import "./search-drawer";
</style>
